import { combineReducers } from 'redux';
import produce from 'immer';
import {
  ProcessScheduleActionType,
  ProcessScheduleAction,
} from '../../actions/processScheduleActions';
import { ProcessSchedule } from '../../types/resources';
import { getEarliestDate } from '../../utils/timeHelper';

interface ProcessScheduleState {
  byId: Record<string, ProcessSchedule>;
  allIds: string[];
  nextEventByProcessId: Record<string, string>;
}

export const initialState: ProcessScheduleState = {
  byId: {},
  allIds: [],
  nextEventByProcessId: {},
};

const byId = (state = initialState.byId, action: ProcessScheduleAction) =>
  produce(state, (draft) => {
    switch (action.type) {
      case ProcessScheduleActionType.GET_PROCESS_SCHEDULES:
        return {};
      case ProcessScheduleActionType.GET_PROCESS_SCHEDULES_OK: {
        action.data.forEach((schedule) => {
          draft[schedule.id] = schedule;
        });

        break;
      }
      case ProcessScheduleActionType.CREATE_PROCESS_SCHEDULE_OK: {
        draft[action.data.id] = action.data;
        break;
      }
      case ProcessScheduleActionType.DELETE_PROCESS_SCHEDULE_OK: {
        delete draft[action.scheduleId];
        break;
      }
      case ProcessScheduleActionType.EDIT_PROCESS_SCHEDULE_OK: {
        draft[action.scheduleId] = action.data;
        break;
      }
      case ProcessScheduleActionType.LIST_WORKSPACE_SCHEDULES:
        return {};
      case ProcessScheduleActionType.LIST_WORKSPACE_SCHEDULES_OK: {
        action.data.forEach((schedule) => {
          draft[schedule.id] = schedule;
        });

        break;
      }
      default:
        return state;
    }
  });

const nextEventByProcessId = (
  state = initialState.nextEventByProcessId,
  action: ProcessScheduleAction,
) =>
  produce(state, (draft) => {
    switch (action.type) {
      case ProcessScheduleActionType.EDIT_PROCESS_SCHEDULE:
        return {};
      case ProcessScheduleActionType.CREATE_PROCESS_SCHEDULE_OK:
      case ProcessScheduleActionType.EDIT_PROCESS_SCHEDULE_OK:
        // No next event set for process
        if (!draft[action.data.target.targetId] && action.data.nextEvent) {
          draft[action.data.target.targetId] = action.data.nextEvent?.notBeforeTs;
          break;
        }

        // Process already has a schedule event
        if (draft[action.data.target.targetId] && action.data.nextEvent) {
          if (draft[action.data.target.targetId] > action.data.nextEvent.notBeforeTs) {
            draft[action.data.target.targetId] = action.data.nextEvent.notBeforeTs;
            break;
          }
        }

        break;

      case ProcessScheduleActionType.GET_PROCESS_SCHEDULES_OK: {
        const events: string[] = [];
        action.data.forEach((schedule) => {
          if (schedule.nextEvent?.notBeforeTs) events.push(schedule.nextEvent?.notBeforeTs);
        });

        if (events.length > 0) draft[action.processId] = getEarliestDate(events);

        break;
      }

      case ProcessScheduleActionType.LIST_WORKSPACE_SCHEDULES:
        return {};
      case ProcessScheduleActionType.LIST_WORKSPACE_SCHEDULES_OK: {
        const eventsByProcessId: Record<string, string[]> = {};
        action.data.forEach((schedule) => {
          if (!eventsByProcessId[schedule.target.targetId]) {
            eventsByProcessId[schedule.target.targetId] = [];
          }

          if (schedule.nextEvent?.notBeforeTs)
            eventsByProcessId[schedule.target.targetId].push(schedule.nextEvent?.notBeforeTs);
        });

        // eslint-disable-next-line no-restricted-syntax
        for (const [processId, events] of Object.entries(eventsByProcessId)) {
          if (events.length > 0) draft[processId] = getEarliestDate(events);
        }

        break;
      }
      default:
        return state;
    }
  });

const allIds = (state = initialState.allIds, action: ProcessScheduleAction) => {
  switch (action.type) {
    case ProcessScheduleActionType.LIST_WORKSPACE_SCHEDULES:
      return [];
    case ProcessScheduleActionType.LIST_WORKSPACE_SCHEDULES_OK:
      return action.data.map((schedule) => schedule.id);
    case ProcessScheduleActionType.GET_PROCESS_SCHEDULES:
      return [];
    case ProcessScheduleActionType.GET_PROCESS_SCHEDULES_OK:
      return action.data.map((schedule) => schedule.id);
    case ProcessScheduleActionType.DELETE_PROCESS_SCHEDULE_OK:
      return state.filter((id) => id !== action.scheduleId);
    case ProcessScheduleActionType.CREATE_PROCESS_SCHEDULE_OK:
      return [...state, action.data.id];
    default:
      return state;
  }
};

export default combineReducers({
  byId,
  allIds,
  nextEventByProcessId,
});
